import React, { FC, useState } from 'react';
import cx from 'classnames';
import { useQueryClient } from '@tanstack/react-query';

import ButtonPencil from 'shared/components/Buttons/ButtonPencil';
import ButtonConfirmCircle from 'shared/components/Buttons/ButtonActionCircle/ButtonConfirmCircle';
import ButtonRejectCircle from 'shared/components/Buttons/ButtonActionCircle/ButtonRejectCircle';

import ReasonForRejectionPopup from 'components/Organization/Opportunities/OpportunityPopups/ReasonForRejectionPopup';
import { API } from 'core/API';

import { ChangeStatusOpportunityRequest, OPPORTUNITY_STATUSES, ADMIN_SEARCH_FILTER_TYPE } from '@joc/api-gateway';
import { TABLE_ITEMS_CLASSNAMES } from 'core/constants';
import cellDefaultStyles from 'shared/components/Table/CellRenderers/CellDefault.module.scss';

import { SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY } from '../hooks/useSuperAdminOpportunities';

type SuperAdminManualOpportunityActionsNewProps = {
	opportunityId: number;
	editClickHandler?: () => void;
	opportunityFilterType: ADMIN_SEARCH_FILTER_TYPE;
};

const SuperAdminManualOpportunityActionsNew: FC<SuperAdminManualOpportunityActionsNewProps> = ({
	opportunityId,
	editClickHandler,
	opportunityFilterType,
}: SuperAdminManualOpportunityActionsNewProps) => {
	const queryClient = useQueryClient();
	const [isShowRejectionPopup, setIsShowRejectionPopup] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const invalidateList = () => {
		queryClient.invalidateQueries({
			queryKey: [SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY, opportunityFilterType],
			exact: false,
		});
	};

	const confirmClickHandler = async () => {
		try {
			setIsLoading(true);
			await API.changeStatusOpportunity(
				undefined,
				ChangeStatusOpportunityRequest.fromJS({
					opportunityId: Number(opportunityId),
					opportunityStatus: OPPORTUNITY_STATUSES.OPEN,
				})
			);
			invalidateList();
		} catch (e) {
			// Legacy flow handles errors via redux; here we keep it silent to avoid UX changes.
		} finally {
			setIsLoading(false);
		}
	};

	const rejectClickHandler = () => setIsShowRejectionPopup(true);

	const handleRejectConfirm = async (reason: string) => {
		const rejectionReason = reason.trim() || undefined;
		try {
			setIsLoading(true);
			await API.changeStatusOpportunity(
				undefined,
				ChangeStatusOpportunityRequest.fromJS({
					opportunityId: Number(opportunityId),
					opportunityStatus: OPPORTUNITY_STATUSES.CANCELED,
					rejectionReason,
				})
			);
			invalidateList();
		} catch (e) {
			// keep silent (no snackbars in legacy manual cell)
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<div
				className={cx(cellDefaultStyles.main, cellDefaultStyles[TABLE_ITEMS_CLASSNAMES.options], {
					[cellDefaultStyles.options__manual]: !!editClickHandler,
				})}
			>
				{editClickHandler ? (
					<ButtonPencil
						clickHandler={editClickHandler}
						parentClassName={!!editClickHandler ? cellDefaultStyles.options__manual__edit : ''}
					/>
				) : null}
				<ButtonConfirmCircle
					parentClassName={!!editClickHandler ? cellDefaultStyles.options__manual__confirm : ''}
					clickHandler={confirmClickHandler}
					disabled={isLoading}
				/>
				<ButtonRejectCircle
					parentClassName={!!editClickHandler ? cellDefaultStyles.options__manual__confirm : ''}
					clickHandler={rejectClickHandler}
					disabled={isLoading}
				/>
			</div>

			{isShowRejectionPopup && (
				<ReasonForRejectionPopup setIsShowPopup={setIsShowRejectionPopup} onConfirm={handleRejectConfirm} />
			)}
		</>
	);
};

export default SuperAdminManualOpportunityActionsNew;
